import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Download, ArrowRight, Star, Users, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DALLAS_NIGHT } from "@/lib/constants";

// DMN custom hero video - optimized MP4 (29MB, 1920x1080)
const HERO_VIDEO_URL = "/videos/hero_video.mp4";
const HERO_VIDEO_POSTER = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&q=80";

const HeroSection = ({ onOpenPopup }) => {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Try to play immediately
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setVideoLoaded(true);
          })
          .catch((error) => {
            console.log("Video autoplay prevented:", error);
            // Try muted autoplay as fallback
            video.muted = true;
            video.play().then(() => setVideoLoaded(true)).catch(() => {
              setVideoError(true);
            });
          });
      }
    }
  }, []);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleVideoError = () => {
    setVideoError(true);
  };

  return (
    <section className="relative min-h-screen flex items-center pt-24 overflow-hidden" data-testid="hero-section">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        {/* Fallback Image (shown while video loads) */}
        <div 
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
          style={{ backgroundImage: `url(${DALLAS_NIGHT})` }}
        />
        
        {/* Video Element */}
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_VIDEO_POSTER}
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        >
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>
        
        {/* Dark Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        
        {/* Additional subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />
      </div>

      {/* Animated Background Accents */}
      <div className="absolute inset-0 overflow-hidden z-[1] pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#00E676]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-[#6366F1]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8 space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00E676]/30 bg-black/50 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-[#00E676]" />
              <span className="text-sm text-[#00E676] font-medium">AEO & GEO Agency in Dallas</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight text-white drop-shadow-2xl">
              Dominate Dallas Search with
              <span className="text-[#00E676]"> AI-Powered SEO</span>
            </h1>

            <p className="text-xl text-gray-200 max-w-xl drop-shadow-lg">
              We help Dallas businesses get found in traditional search, AI assistants, and voice search. 
              Future-proof your digital presence with our AEO, GEO, and Local SEO expertise.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="btn-ninja flex items-center gap-2 shadow-lg shadow-[#00E676]/20"
                onClick={() => onOpenPopup('ebook')}
                data-testid="hero-cta-ebook"
              >
                <Download className="w-5 h-5" />
                Download 2026 AEO Tactics
              </Button>
              <Link to="/contact">
                <Button className="btn-outline flex items-center gap-2 backdrop-blur-sm bg-white/10 border-white/30 hover:bg-white/20" data-testid="hero-cta-audit">
                  Get Free SEO Audit
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-8 pt-8 border-t border-white/20">
              <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-[#00E676]" />
                <span className="text-sm text-gray-200">5.0 Google Rating</span>
              </div>
              <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full">
                <Users className="w-5 h-5 text-[#00E676]" />
                <span className="text-sm text-gray-200">Local Dallas–Fort Worth Team</span>
              </div>
              <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full">
                <TrendingUp className="w-5 h-5 text-[#00E676]" />
                <span className="text-sm text-gray-200">AEO · GEO · Local SEO</span>
              </div>
            </div>
          </div>

          {/* Right side - Location Badge */}
          <div className="lg:col-span-4 relative hidden lg:block">
            <div className="relative rounded-2xl overflow-hidden border border-white/20 bg-black/40 backdrop-blur-md p-6">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00E676] to-[#6366F1]" />
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-full bg-[#00E676]/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#00E676]" />
                </div>
                <p className="text-white font-bold text-xl">Serving Dallas-Fort Worth</p>
                <p className="text-gray-300 text-sm">
                  Local SEO experts dedicated to helping DFW businesses dominate search results and grow their online presence.
                </p>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-[#00E676] text-sm font-medium">📍 3333 Harry Hines Blvd, Dallas TX</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
